export type Language = 'en' | 'de' | 'it' | 'fr' | 'es' | 'ja' | 'ko' | 'zh';

// Inquiry type options – keep in sync with DB defaults
export const INQUIRY_TYPES = [
  'general_question',
  'instrument_purchase',
  'restoration_repair',
  'new_build',
] as const;
export type InquiryType = typeof INQUIRY_TYPES[number];

export interface Translations {
  brandName: string;
  tagline: string;
  subline: string;
  visitInstagram: string;
  visitFacebook: string;
  followText: string;
  copyright: string;
  imageCaption: string;
  metaTitle: string;
  metaDescription: string;
  // Navigation
  navHome: string;
  navAbout: string;
  navAtelier: string;
  navInstruments: string;
  navRestorations: string;
  navGallery: string;
  navContact: string;
  // About / Atelier page
  aboutTitle: string;
  aboutIntro: string;
  aboutPhilosophy: string;
  aboutPhilosophyText: string;
  aboutCraftsmanship: string;
  aboutCraftsmanshipText: string;
  atelierTagline: string;
  atelierPageTitle: string;
  atelierPageIntro: string;
  atelierMakerTitle: string;
  atelierMakerText1: string;
  atelierMakerText2: string;
  atelierPhilosophyTitle: string;
  atelierPhilosophyText1: string;
  atelierPhilosophyText2: string;
  atelierProcessTitle: string;
  atelierProcessText: string;
  atelierProcessStep1: string;
  atelierProcessStep2: string;
  atelierProcessStep3: string;
  atelierRestorationTitle: string;
  atelierRestorationText: string;
  // Instruments page
  instrumentsTitle: string;
  instrumentsIntro: string;
  violinTitle: string;
  violinText: string;
  violaTitle: string;
  violaText: string;
  celloTitle: string;
  celloText: string;
  bassTitle: string;
  bassText: string;
  // Restorations page
  restorationsTitle: string;
  restorationsIntro: string;
  restorationsText: string;
  // Gallery page
  galleryTitle: string;
  galleryIntro: string;
  galleryEmpty: string;
  // Contact page
  contactTitle: string;
  contactIntro: string;
  contactName: string;
  contactEmail: string;
  contactSubject: string;
  contactMessage: string;
  contactSend: string;
  contactSuccess: string;
  contactError: string;
  contactRateLimit: string;
  contactNameRequired: string;
  contactEmailRequired: string;
  contactEmailInvalid: string;
  contactMessageRequired: string;
  contactInquiryType: string;
  contactCountry: string;
  contactSending: string;
  contactSuccessReference: string;
  contactSuccessNext: string;
  // Inquiry type labels
  inquiryTypeGeneral: string;
  inquiryTypePurchase: string;
  inquiryTypeRestoration: string;
  inquiryTypeNewBuild: string;
  // Filters
  filterAll: string;
  filterAvailable: string;
  filterViolin: string;
  filterViola: string;
  filterCello: string;
  filterBass: string;
  instrumentsEmpty: string;
  inquireNow: string;
  view360: string;
  view360Hint: string;
  // Instrument detail specs
  specType: string;
  specModel: string;
  specTop: string;
  specBack: string;
  specVarnish: string;
  specBodyLength: string;
  specStringLength: string;
  specYear: string;
  referenceLabel: string;
  priceOnRequest: string;
  soldLabel: string;
  availableLabel: string;
  backToInstruments: string;
  // Instrument inquiry modal
  inquiryTitle: string;
  inquirySuccess: string;
  inquiryNamePlaceholder: string;
  inquiryEmailPlaceholder: string;
  inquiryMessagePlaceholder: string;
  inquirySend: string;
  inquiryClose: string;
  // Homepage redesign
  heroCtaContact: string;
  brandStatementHeadline: string;
  brandStatementText: string;
  brandStatementText2: string;
  futureInstrumentsLabel: string;
  futureInstrumentsDesc: string;
  futureSelectedWorksLabel: string;
  futureSelectedWorksDesc: string;
  futureAtelierLabel: string;
  futureAtelierDesc: string;
  futureAboutLabel: string;
  futureAboutDesc: string;
  // Featured instruments
  featuredSectionTitle: string;
  // Atelier teaser
  atelierTeaserTitle: string;
  atelierTeaserText: string;
  atelierTeaserCta: string;
  // Contact teaser
  contactTeaserTitle: string;
  contactTeaserText: string;
  contactTeaserCta: string;
  // Legal pages
  impressumTitle: string;
  impressumResponsible: string;
  impressumContact: string;
  impressumLiabilityContent: string;
  impressumLiabilityContentText: string;
  impressumLiabilityLinks: string;
  impressumLiabilityLinksText: string;
  impressumCopyright: string;
  impressumCopyrightText: string;
  datenschutzTitle: string;
  datenschutzGeneral: string;
  datenschutzGeneralText: string;
  datenschutzCollection: string;
  datenschutzCollectionText: string;
  datenschutzForms: string;
  datenschutzFormsText: string;
  datenschutzCookies: string;
  datenschutzCookiesText: string;
  datenschutzRights: string;
  datenschutzRightsText: string;
  // Footer
  footerImprint: string;
  footerPrivacy: string;
  // Error
  errorTitle: string;
  errorText: string;
  errorRetry: string;
  // Instrument detail enhancement
  instrumentInquiryCta: string;
  specsIdentity: string;
  specsConstruction: string;
  moreInstruments: string;
  exploreByType: string;
}

export const translations: Record<Language, Translations> = {
  en: {
    brandName: 'Szoradi Instruments',
    tagline: 'Fine Violins, Violas, Cellos & Double Basses — Handcrafted in Berlin, Germany',
    subline: 'Artisan string instruments made for musicians worldwide.',
    visitInstagram: 'Visit Instagram',
    visitFacebook: 'Visit Facebook',
    followText: 'Follow Szoradi Instruments on social media to discover craftsmanship, sound, and creation.',
    copyright: '© 2025 Szoradi Instruments. All rights reserved.',
    imageCaption: 'Cello after Carlo Tononi',
    metaTitle: 'Szoradi Instruments | Fine Violins, Violas, Cellos & Double Basses – Handcrafted in Berlin, Germany',
    metaDescription: 'Szoradi Instruments builds fine handcrafted violins, violas, cellos, and double basses in Berlin, Germany. Authentic craftsmanship for musicians worldwide.',
    navHome: 'Home',
    navAbout: 'About',
    navAtelier: 'Atelier',
    navInstruments: 'Instruments',
    navRestorations: 'Restorations',
    navGallery: 'Gallery',
    navContact: 'Contact',
    aboutTitle: 'About the Workshop',
    aboutIntro: 'Szoradi Instruments is a Berlin-based workshop dedicated to the art of building fine string instruments by hand.',
    aboutPhilosophy: 'Philosophy',
    aboutPhilosophyText: 'Every instrument is a unique creation, shaped by tradition and refined through decades of experience. We believe that a truly great instrument must sing with its own voice — resonant, expressive, and alive.',
    aboutCraftsmanship: 'Craftsmanship',
    aboutCraftsmanshipText: 'Using only the finest tonewoods and time-honored techniques, each instrument is meticulously crafted from start to finish. Every curve, every joint, every layer of varnish is applied with care and intention.',
    atelierTagline: 'The Workshop',
    atelierPageTitle: 'Where Sound Takes Shape',
    atelierPageIntro: 'A Berlin atelier dedicated to the art of building fine string instruments by hand — with precision, patience, and an uncompromising pursuit of tonal excellence.',
    atelierMakerTitle: 'The Maker',
    atelierMakerText1: 'Every instrument that leaves this workshop carries the mark of a singular vision: to create string instruments that are not merely played, but deeply felt. Rooted in the traditions of classical Italian lutherie, the work here is guided by an intimate understanding of wood, acoustics, and the demands of professional musicians.',
    atelierMakerText2: 'Years of training, study of historical masterworks, and hands-on experience with hundreds of fine instruments have shaped an approach that balances reverence for tradition with a modern maker\'s eye for refinement and precision.',
    atelierPhilosophyTitle: 'Philosophy',
    atelierPhilosophyText1: 'A great instrument must sing with its own voice — resonant, expressive, alive. This belief guides every decision in the workshop, from the selection of tonewoods to the final layers of varnish.',
    atelierPhilosophyText2: 'There are no shortcuts. Each instrument is built entirely by hand, one at a time, with the kind of focused attention that only a small atelier can provide. The goal is never quantity — it is character, projection, and a sound that stays with the listener.',
    atelierProcessTitle: 'The Process',
    atelierProcessText: 'Building a fine instrument is a journey measured in months, not days. From carefully chosen spruce and maple through arching, graduation, and varnishing — every stage demands precision and instinct in equal measure.',
    atelierProcessStep1: 'Selection & Preparation',
    atelierProcessStep2: 'Carving & Graduation',
    atelierProcessStep3: 'Varnish & Voicing',
    atelierRestorationTitle: 'Conservation & Repair',
    atelierRestorationText: 'In addition to new making, the workshop offers expert restoration and repair services for fine string instruments. From structural conservation to varnish touch-up, every intervention is carried out with the same care and precision as new work — always respecting the instrument\'s history and original character.',
    instrumentsTitle: 'Instruments',
    instrumentsIntro: 'Each instrument is built entirely by hand, following the great traditions of classical instrument making.',
    violinTitle: 'Violins',
    violinText: 'Built on the models of the great Italian masters, our violins offer a warm, projecting tone with exceptional playability.',
    violaTitle: 'Violas',
    violaText: 'Rich, dark, and sonorous — our violas are crafted for depth of sound and comfort for the performer.',
    celloTitle: 'Cellos',
    celloText: 'Powerful and singing, our cellos are designed to fill concert halls with warmth and clarity.',
    bassTitle: 'Double Basses',
    bassText: 'From orchestral to solo repertoire, our double basses deliver deep resonance and remarkable responsiveness.',
    restorationsTitle: 'Restorations',
    restorationsIntro: 'Preserving the legacy of fine instruments through expert restoration.',
    restorationsText: 'We offer professional restoration services for antique and modern string instruments. From structural repairs to varnish restoration, every instrument receives the care and expertise it deserves. Our goal is to preserve the original character and tonal qualities while ensuring the instrument can continue to be played for generations.',
    galleryTitle: 'Gallery',
    galleryIntro: 'A selection of instruments from the workshop.',
    galleryEmpty: 'New works are being prepared. Please visit again soon.',
    contactTitle: 'Contact',
    contactIntro: 'Interested in commissioning an instrument or have a question? We would love to hear from you.',
    contactName: 'Name',
    contactEmail: 'Email',
    contactSubject: 'Subject',
    contactMessage: 'Message',
    contactSend: 'Send Message',
    contactSuccess: 'Your message has been sent successfully. We will respond as soon as possible.',
    contactError: 'An error occurred. Please try again.',
    contactRateLimit: 'Too many requests. Please try again later.',
    contactNameRequired: 'Please enter your name.',
    contactEmailRequired: 'Please enter your email address.',
    contactEmailInvalid: 'Please enter a valid email address.',
    contactMessageRequired: 'Please enter your message.',
    contactInquiryType: 'Inquiry Type',
    contactCountry: 'Country (optional)',
    contactSending: 'Sending…',
    contactSuccessReference: 'Your reference code',
    contactSuccessNext: 'We will respond within 48 hours.',
    inquiryTypeGeneral: 'General Question',
    inquiryTypePurchase: 'Instrument Purchase',
    inquiryTypeRestoration: 'Restoration & Repair',
    inquiryTypeNewBuild: 'New Build',
    filterAll: 'All',
    filterViolin: 'Violins',
    filterViola: 'Violas',
    filterCello: 'Cellos',
    filterBass: 'Double Basses',
    filterAvailable: 'Available',
    instrumentsEmpty: 'New instruments are being prepared. Please visit again soon.',
    inquireNow: 'Inquire',
    view360: '360° View',
    view360Hint: 'Interactive 360° view available',
    specType: 'Type',
    specModel: 'Model',
    specTop: 'Top',
    specBack: 'Back & Ribs',
    specVarnish: 'Varnish',
    specBodyLength: 'Body Length',
    specStringLength: 'String Length',
    specYear: 'Year',
    referenceLabel: 'Ref. No.',
    priceOnRequest: 'Price on Request',
    soldLabel: 'Sold',
    availableLabel: 'Available',
    backToInstruments: 'Back to Instruments',
    inquiryTitle: 'Inquiry About This Instrument',
    inquirySuccess: 'Your inquiry has been sent successfully.',
    inquiryNamePlaceholder: 'Your Name',
    inquiryEmailPlaceholder: 'Your Email',
    inquiryMessagePlaceholder: 'Your message...',
    inquirySend: 'Send Inquiry',
    inquiryClose: 'Close',
    heroCtaContact: 'Make an Inquiry',
    brandStatementHeadline: 'Crafted for Sound',
    brandStatementText: 'Every instrument begins with a careful selection of the finest tonewoods — spruce and maple, aged and resonant. From there, each curve, each thickness, each layer of varnish is shaped by hand with a single purpose: to create an instrument that sings.',
    brandStatementText2: 'The work in this atelier follows the traditions of the great Italian masters, refined through years of experience and an uncompromising pursuit of tonal quality.',
    futureInstrumentsLabel: 'Instruments',
    futureInstrumentsDesc: 'Violins, violas, cellos, and double basses — each built entirely by hand.',
    futureSelectedWorksLabel: 'Selected Works',
    futureSelectedWorksDesc: 'A curated view of completed instruments and their details.',
    futureAtelierLabel: 'The Atelier',
    futureAtelierDesc: 'Tools, materials, and the process behind every instrument.',
    futureAboutLabel: 'About',
    futureAboutDesc: 'The maker, the philosophy, and the pursuit of exceptional sound.',
    featuredSectionTitle: 'Featured Instruments',
    atelierTeaserTitle: 'The Atelier',
    atelierTeaserText: 'Step inside a Berlin workshop where tradition meets precision. Every instrument begins here — with carefully selected tonewoods and the hands of a dedicated maker.',
    atelierTeaserCta: 'Discover the Atelier',
    contactTeaserTitle: 'Get in Touch',
    contactTeaserText: 'Interested in commissioning an instrument, a restoration, or simply have a question? We look forward to hearing from you.',
    contactTeaserCta: 'Contact Us',
    impressumTitle: 'Legal Notice',
    impressumResponsible: 'Responsible according to § 5 TMG',
    impressumContact: 'Contact',
    impressumLiabilityContent: 'Liability for Content',
    impressumLiabilityContentText: 'As a service provider, we are responsible for our own content on these pages in accordance with general legislation pursuant to § 7 (1) TMG. According to §§ 8 to 10 TMG, however, we are not obligated to monitor transmitted or stored third-party information or to investigate circumstances that indicate illegal activity.',
    impressumLiabilityLinks: 'Liability for Links',
    impressumLiabilityLinksText: 'Our website contains links to external third-party websites over whose content we have no influence. Therefore, we cannot accept any liability for this third-party content. The respective provider or operator of the pages is always responsible for the content of the linked pages.',
    impressumCopyright: 'Copyright',
    impressumCopyrightText: 'The content and works on these pages created by the site operator are subject to copyright law. Duplication, processing, distribution, or any form of commercialization of such material beyond the scope of copyright law requires the prior written consent of the respective author or creator.',
    datenschutzTitle: 'Privacy Policy',
    datenschutzGeneral: 'General Information',
    datenschutzGeneralText: 'The protection of your personal data is very important to us. We treat your personal data confidentially and in accordance with the applicable data protection regulations and this privacy policy.',
    datenschutzCollection: 'Data Collection',
    datenschutzCollectionText: 'When you visit our website, certain data is automatically collected for technical reasons (e.g. IP address, browser type, access time). This data is used solely for ensuring the technical operation of the website and is deleted after use.',
    datenschutzForms: 'Contact Forms',
    datenschutzFormsText: 'When you contact us via form, the data you provide (name, email address, message) will be stored for the purpose of processing your inquiry. This data will not be shared with third parties without your consent.',
    datenschutzCookies: 'Cookies',
    datenschutzCookiesText: 'This website uses only technically necessary cookies required for the operation of the website. No tracking or analytics cookies are used.',
    datenschutzRights: 'Your Rights',
    datenschutzRightsText: 'You have the right to request information about the personal data stored about you at any time. You also have the right to correction, deletion, and restriction of processing of your personal data as well as the right to data portability.',
    footerImprint: 'Legal Notice',
    footerPrivacy: 'Privacy Policy',
    errorTitle: 'Something went wrong',
    errorText: 'An unexpected error occurred. Please try again or return to the homepage.',
    errorRetry: 'Try Again',
    instrumentInquiryCta: 'Interested in this instrument?',
    specsIdentity: 'Identity',
    specsConstruction: 'Construction',
    moreInstruments: 'More Instruments',
    exploreByType: 'Explore by Type',
  },
  de: {
    brandName: 'Szoradi Instruments',
    tagline: 'Feine Violinen, Bratschen, Celli & Kontrabässe — Handgefertigt in Berlin, Deutschland',
    subline: 'Meisterhafte Streichinstrumente für Musiker weltweit.',
    visitInstagram: 'Instagram besuchen',
    visitFacebook: 'Facebook besuchen',
    followText: 'Folgen Sie Szoradi Instruments in den sozialen Medien und entdecken Sie Handwerkskunst, Klang und Kreation.',
    copyright: '© 2025 Szoradi Instruments. Alle Rechte vorbehalten.',
    imageCaption: 'Cello nach Carlo Tononi',
    metaTitle: 'Szoradi Instruments | Feine Violinen, Bratschen, Celli & Kontrabässe – Handgefertigt in Berlin',
    metaDescription: 'Szoradi Instruments fertigt feine handgefertigte Violinen, Bratschen, Celli und Kontrabässe in Berlin, Deutschland. Authentische Handwerkskunst für Musiker weltweit.',
    navHome: 'Startseite',
    navAbout: 'Werkstatt',
    navAtelier: 'Atelier',
    navInstruments: 'Instrumente',
    navRestorations: 'Restaurierungen',
    navGallery: 'Galerie',
    navContact: 'Kontakt',
    aboutTitle: 'Über die Werkstatt',
    aboutIntro: 'Szoradi Instruments ist eine Berliner Werkstatt, die sich der Kunst des handgefertigten Streichinstrumentenbaus widmet.',
    aboutPhilosophy: 'Philosophie',
    aboutPhilosophyText: 'Jedes Instrument ist eine einzigartige Schöpfung, geformt durch Tradition und verfeinert durch jahrzehntelange Erfahrung. Wir glauben, dass ein wahrhaft großes Instrument mit seiner eigenen Stimme singen muss — resonant, ausdrucksvoll und lebendig.',
    aboutCraftsmanship: 'Handwerkskunst',
    aboutCraftsmanshipText: 'Unter Verwendung nur der feinsten Tonhölzer und bewährter Techniken wird jedes Instrument von Anfang bis Ende sorgfältig gefertigt. Jede Kurve, jede Fuge, jede Lackschicht wird mit Sorgfalt und Intention aufgetragen.',
    atelierTagline: 'Die Werkstatt',
    atelierPageTitle: 'Wo Klang Gestalt annimmt',
    atelierPageIntro: 'Ein Berliner Atelier, das sich der Kunst des handgefertigten Streichinstrumentenbaus widmet — mit Präzision, Geduld und dem kompromisslosen Streben nach klanglicher Exzellenz.',
    atelierMakerTitle: 'Der Geigenbauer',
    atelierMakerText1: 'Jedes Instrument, das diese Werkstatt verlässt, trägt die Handschrift einer klaren Vision: Streichinstrumente zu schaffen, die nicht nur gespielt, sondern zutiefst empfunden werden. Verwurzelt in den Traditionen der klassischen italienischen Geigenbaukunst, wird die Arbeit hier von einem tiefen Verständnis für Holz, Akustik und die Anforderungen professioneller Musiker geleitet.',
    atelierMakerText2: 'Jahre der Ausbildung, das Studium historischer Meisterwerke und die praktische Erfahrung mit Hunderten feiner Instrumente haben einen Ansatz geformt, der Ehrfurcht vor der Tradition mit dem Blick eines modernen Geigenbauers für Verfeinerung und Präzision verbindet.',
    atelierPhilosophyTitle: 'Philosophie',
    atelierPhilosophyText1: 'Ein großes Instrument muss mit seiner eigenen Stimme singen — resonant, ausdrucksvoll, lebendig. Diese Überzeugung leitet jede Entscheidung in der Werkstatt, von der Auswahl der Tonhölzer bis zu den letzten Lackschichten.',
    atelierPhilosophyText2: 'Es gibt keine Abkürzungen. Jedes Instrument wird vollständig von Hand gebaut, eines nach dem anderen, mit der Art von konzentrierter Aufmerksamkeit, die nur ein kleines Atelier bieten kann. Das Ziel ist niemals Quantität — es ist Charakter, Projektion und ein Klang, der beim Zuhörer bleibt.',
    atelierProcessTitle: 'Der Prozess',
    atelierProcessText: 'Der Bau eines feinen Instruments ist eine Reise, die in Monaten gemessen wird, nicht in Tagen. Vom sorgfältig ausgewählten Fichten- und Ahornholz über Wölbung, Graduation und Lackierung — jede Phase verlangt Präzision und Instinkt gleichermaßen.',
    atelierProcessStep1: 'Auswahl & Vorbereitung',
    atelierProcessStep2: 'Schnitzarbeit & Graduation',
    atelierProcessStep3: 'Lack & Stimmgebung',
    atelierRestorationTitle: 'Konservierung & Reparatur',
    atelierRestorationText: 'Neben dem Neubau bietet die Werkstatt fachkundige Restaurierungs- und Reparaturleistungen für feine Streichinstrumente. Von der strukturellen Konservierung bis zur Lackretusche — jeder Eingriff wird mit derselben Sorgfalt und Präzision wie eine Neufertigung ausgeführt, stets unter Wahrung der Geschichte und des Originalcharakters des Instruments.',
    instrumentsTitle: 'Instrumente',
    instrumentsIntro: 'Jedes Instrument wird vollständig von Hand gebaut und folgt den großen Traditionen des klassischen Instrumentenbaus.',
    violinTitle: 'Violinen',
    violinText: 'Nach den Modellen der großen italienischen Meister gebaut, bieten unsere Violinen einen warmen, tragfähigen Ton mit außergewöhnlicher Spielbarkeit.',
    violaTitle: 'Bratschen',
    violaText: 'Reich, dunkel und klangvoll — unsere Bratschen sind für Klangtiefe und Komfort des Spielers gefertigt.',
    celloTitle: 'Celli',
    celloText: 'Kraftvoll und singend, unsere Celli sind darauf ausgelegt, Konzertsäle mit Wärme und Klarheit zu füllen.',
    bassTitle: 'Kontrabässe',
    bassText: 'Vom Orchester- bis zum Solorepertoire liefern unsere Kontrabässe tiefe Resonanz und bemerkenswerte Ansprache.',
    restorationsTitle: 'Restaurierungen',
    restorationsIntro: 'Bewahrung des Erbes feiner Instrumente durch fachgerechte Restaurierung.',
    restorationsText: 'Wir bieten professionelle Restaurierungsdienstleistungen für antike und moderne Streichinstrumente. Von strukturellen Reparaturen bis zur Lackrestauration erhält jedes Instrument die Pflege und Expertise, die es verdient. Unser Ziel ist es, den ursprünglichen Charakter und die klanglichen Qualitäten zu bewahren und gleichzeitig sicherzustellen, dass das Instrument über Generationen hinweg gespielt werden kann.',
    galleryTitle: 'Galerie',
    galleryIntro: 'Eine Auswahl von Instrumenten aus der Werkstatt.',
    galleryEmpty: 'Neue Arbeiten werden vorbereitet. Bitte besuchen Sie uns bald wieder.',
    contactTitle: 'Kontakt',
    contactIntro: 'Interesse an einem Instrument oder eine Frage? Wir freuen uns auf Ihre Nachricht.',
    contactName: 'Name',
    contactEmail: 'E-Mail',
    contactSubject: 'Betreff',
    contactMessage: 'Nachricht',
    contactSend: 'Nachricht senden',
    contactSuccess: 'Ihre Nachricht wurde erfolgreich gesendet. Wir antworten so bald wie möglich.',
    contactError: 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.',
    contactRateLimit: 'Zu viele Anfragen. Bitte versuchen Sie es später erneut.',
    contactNameRequired: 'Bitte geben Sie Ihren Namen ein.',
    contactEmailRequired: 'Bitte geben Sie Ihre E-Mail-Adresse ein.',
    contactEmailInvalid: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.',
    contactMessageRequired: 'Bitte geben Sie Ihre Nachricht ein.',
    contactInquiryType: 'Art der Anfrage',
    contactCountry: 'Land (optional)',
    contactSending: 'Wird gesendet…',
    contactSuccessReference: 'Ihre Referenznummer',
    contactSuccessNext: 'Wir antworten innerhalb von 48 Stunden.',
    inquiryTypeGeneral: 'Allgemeine Frage',
    inquiryTypePurchase: 'Instrumentenkauf',
    inquiryTypeRestoration: 'Restaurierung & Reparatur',
    inquiryTypeNewBuild: 'Neubau',
    filterAll: 'Alle',
    filterViolin: 'Violinen',
    filterViola: 'Bratschen',
    filterCello: 'Celli',
    filterBass: 'Kontrabässe',
    filterAvailable: 'Verfügbar',
    instrumentsEmpty: 'Neue Instrumente werden vorbereitet. Bitte besuchen Sie uns bald wieder.',
    inquireNow: 'Anfragen',
    view360: '360°-Ansicht',
    view360Hint: 'Interaktive 360°-Ansicht verfügbar',
    specType: 'Typ',
    specModel: 'Modell',
    specTop: 'Decke',
    specBack: 'Boden & Zargen',
    specVarnish: 'Lackierung',
    specBodyLength: 'Korpusmaß',
    specStringLength: 'Saitenlänge',
    specYear: 'Baujahr',
    referenceLabel: 'Ref.-Nr.',
    priceOnRequest: 'Preis auf Anfrage',
    soldLabel: 'Verkauft',
    availableLabel: 'Verfügbar',
    backToInstruments: 'Zurück zu Instrumente',
    inquiryTitle: 'Anfrage zu diesem Instrument',
    inquirySuccess: 'Ihre Anfrage wurde erfolgreich gesendet.',
    inquiryNamePlaceholder: 'Ihr Name',
    inquiryEmailPlaceholder: 'Ihre E-Mail',
    inquiryMessagePlaceholder: 'Ihre Nachricht...',
    inquirySend: 'Anfrage senden',
    inquiryClose: 'Schließen',
    heroCtaContact: 'Anfrage stellen',
    brandStatementHeadline: 'Gebaut für den Klang',
    brandStatementText: 'Jedes Instrument beginnt mit der sorgfältigen Auswahl feinster Tonhölzer — Fichte und Ahorn, gelagert und resonant. Von dort wird jede Wölbung, jede Stärke, jede Lackschicht von Hand geformt — mit einem einzigen Ziel: ein Instrument zu schaffen, das singt.',
    brandStatementText2: 'Die Arbeit in diesem Atelier folgt den Traditionen der großen italienischen Meister, verfeinert durch jahrelange Erfahrung und das kompromisslose Streben nach klanglicher Qualität.',
    futureInstrumentsLabel: 'Instrumente',
    futureInstrumentsDesc: 'Violinen, Bratschen, Celli und Kontrabässe — jedes vollständig von Hand gebaut.',
    futureSelectedWorksLabel: 'Ausgewählte Arbeiten',
    futureSelectedWorksDesc: 'Ein kuratierter Blick auf fertiggestellte Instrumente und ihre Details.',
    futureAtelierLabel: 'Das Atelier',
    futureAtelierDesc: 'Werkzeuge, Materialien und der Prozess hinter jedem Instrument.',
    futureAboutLabel: 'Über',
    futureAboutDesc: 'Der Geigenbauer, die Philosophie und das Streben nach außergewöhnlichem Klang.',
    featuredSectionTitle: 'Ausgewählte Instrumente',
    atelierTeaserTitle: 'Das Atelier',
    atelierTeaserText: 'Einblick in eine Berliner Werkstatt, in der Tradition auf Präzision trifft. Jedes Instrument beginnt hier — mit sorgfältig ausgewählten Tonhölzern und den Händen eines engagierten Geigenbauers.',
    atelierTeaserCta: 'Das Atelier entdecken',
    contactTeaserTitle: 'Kontakt aufnehmen',
    contactTeaserText: 'Interesse an einem Instrument, einer Restaurierung oder einfach eine Frage? Wir freuen uns auf Ihre Nachricht.',
    contactTeaserCta: 'Kontakt',
    impressumTitle: 'Impressum',
    impressumResponsible: 'Angaben gemäß § 5 TMG',
    impressumContact: 'Kontakt',
    impressumLiabilityContent: 'Haftung für Inhalte',
    impressumLiabilityContentText: 'Als Diensteanbieter sind wir gemäß § 7 Abs.1 TMG für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 TMG sind wir als Diensteanbieter jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen.',
    impressumLiabilityLinks: 'Haftung für Links',
    impressumLiabilityLinksText: 'Unser Angebot enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen.',
    impressumCopyright: 'Urheberrecht',
    impressumCopyrightText: 'Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem deutschen Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechtes bedürfen der schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers.',
    datenschutzTitle: 'Datenschutzerklärung',
    datenschutzGeneral: 'Allgemeine Hinweise',
    datenschutzGeneralText: 'Der Schutz Ihrer persönlichen Daten ist uns sehr wichtig. Wir behandeln Ihre personenbezogenen Daten vertraulich und entsprechend der gesetzlichen Datenschutzvorschriften sowie dieser Datenschutzerklärung.',
    datenschutzCollection: 'Datenerfassung',
    datenschutzCollectionText: 'Beim Besuch unserer Website werden aus technischen Gründen bestimmte Daten automatisch erfasst (z.B. IP-Adresse, Browsertyp, Zugriffszeit). Diese Daten dienen ausschließlich der technischen Bereitstellung der Website und werden nach der Nutzung gelöscht.',
    datenschutzForms: 'Kontaktformulare',
    datenschutzFormsText: 'Wenn Sie uns über ein Formular kontaktieren, werden die von Ihnen angegebenen Daten (Name, E-Mail-Adresse, Nachricht) zum Zwecke der Bearbeitung Ihrer Anfrage gespeichert. Diese Daten werden ohne Ihre Einwilligung nicht an Dritte weitergegeben.',
    datenschutzCookies: 'Cookies',
    datenschutzCookiesText: 'Diese Website verwendet ausschließlich technisch notwendige Cookies, die für den Betrieb der Website erforderlich sind. Es werden keine Tracking- oder Analyse-Cookies verwendet.',
    datenschutzRights: 'Ihre Rechte',
    datenschutzRightsText: 'Sie haben jederzeit das Recht auf Auskunft über die bei uns gespeicherten personenbezogenen Daten. Ebenso haben Sie das Recht auf Berichtigung, Löschung und Einschränkung der Verarbeitung sowie das Recht auf Datenübertragbarkeit.',
    footerImprint: 'Impressum',
    footerPrivacy: 'Datenschutz',
    errorTitle: 'Etwas ist schiefgelaufen',
    errorText: 'Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut oder kehren Sie zur Startseite zurück.',
    errorRetry: 'Erneut versuchen',
    instrumentInquiryCta: 'Interesse an diesem Instrument?',
    specsIdentity: 'Identität',
    specsConstruction: 'Konstruktion',
    moreInstruments: 'Weitere Instrumente',
    exploreByType: 'Nach Typ entdecken',
  },
  it: {
    brandName: 'Szoradi Instruments',
    tagline: 'Violini, Viole, Violoncelli & Contrabbassi Pregiati — Fatti a Mano a Berlino, Germania',
    subline: 'Strumenti ad arco artigianali per musicisti di tutto il mondo.',
    visitInstagram: 'Visita Instagram',
    visitFacebook: 'Visita Facebook',
    followText: 'Segui Szoradi Instruments sui social media per scoprire artigianato, suono e creazione.',
    copyright: '© 2025 Szoradi Instruments. Tutti i diritti riservati.',
    imageCaption: 'Violoncello da Carlo Tononi',
    metaTitle: 'Szoradi Instruments | Violini, Viole, Violoncelli & Contrabbassi Pregiati – Fatti a Mano a Berlino',
    metaDescription: 'Szoradi Instruments costruisce violini, viole, violoncelli e contrabbassi pregiati fatti a mano a Berlino, Germania. Artigianato autentico per musicisti di tutto il mondo.',
    navHome: 'Home',
    navAbout: 'Laboratorio',
    navAtelier: 'Atelier',
    navInstruments: 'Strumenti',
    navRestorations: 'Restauri',
    navGallery: 'Galleria',
    navContact: 'Contatto',
    aboutTitle: 'Il Laboratorio',
    aboutIntro: 'Szoradi Instruments è un laboratorio berlinese dedicato all\'arte della costruzione di strumenti ad arco fatti a mano.',
    aboutPhilosophy: 'Filosofia',
    aboutPhilosophyText: 'Ogni strumento è una creazione unica, plasmata dalla tradizione e raffinata attraverso decenni di esperienza. Crediamo che un grande strumento debba cantare con la propria voce — risonante, espressivo e vivo.',
    aboutCraftsmanship: 'Artigianato',
    aboutCraftsmanshipText: 'Utilizzando solo i migliori legni tonali e tecniche collaudate, ogni strumento è meticolosamente realizzato dall\'inizio alla fine. Ogni curva, ogni giuntura, ogni strato di vernice è applicato con cura e intenzione.',
    atelierTagline: 'Il Laboratorio',
    atelierPageTitle: 'Dove il suono prende forma',
    atelierPageIntro: 'Un atelier berlinese dedicato all\'arte della costruzione di strumenti ad arco pregiati — con precisione, pazienza e una ricerca instancabile dell\'eccellenza sonora.',
    atelierMakerTitle: 'Il Liutaio',
    atelierMakerText1: 'Ogni strumento che lascia questo laboratorio porta il segno di una visione chiara: creare strumenti ad arco che non vengano semplicemente suonati, ma profondamente sentiti.',
    atelierMakerText2: 'Anni di formazione, lo studio di capolavori storici e l\'esperienza pratica con centinaia di strumenti pregiati hanno plasmato un approccio che bilancia il rispetto per la tradizione con la precisione di un liutaio moderno.',
    atelierPhilosophyTitle: 'Filosofia',
    atelierPhilosophyText1: 'Un grande strumento deve cantare con la propria voce — risonante, espressivo, vivo. Questa convinzione guida ogni decisione nel laboratorio.',
    atelierPhilosophyText2: 'Non ci sono scorciatoie. Ogni strumento è costruito interamente a mano, uno alla volta, con l\'attenzione concentrata che solo un piccolo atelier può offrire.',
    atelierProcessTitle: 'Il Processo',
    atelierProcessText: 'Costruire uno strumento pregiato è un viaggio che si misura in mesi, non in giorni.',
    atelierProcessStep1: 'Selezione e preparazione',
    atelierProcessStep2: 'Intaglio e graduazione',
    atelierProcessStep3: 'Verniciatura e messa a punto',
    atelierRestorationTitle: 'Conservazione e riparazione',
    atelierRestorationText: 'Oltre alla costruzione, il laboratorio offre servizi esperti di restauro e riparazione per strumenti ad arco pregiati.',
    instrumentsTitle: 'Strumenti',
    instrumentsIntro: 'Ogni strumento è costruito interamente a mano, seguendo le grandi tradizioni della liuteria classica.',
    violinTitle: 'Violini',
    violinText: 'Costruiti sui modelli dei grandi maestri italiani, i nostri violini offrono un tono caldo e proiettante con un\'eccezionale suonabilità.',
    violaTitle: 'Viole',
    violaText: 'Ricche, scure e sonore — le nostre viole sono create per la profondità del suono e il comfort dell\'esecutore.',
    celloTitle: 'Violoncelli',
    celloText: 'Potenti e cantanti, i nostri violoncelli sono progettati per riempire le sale da concerto di calore e chiarezza.',
    bassTitle: 'Contrabbassi',
    bassText: 'Dal repertorio orchestrale a quello solistico, i nostri contrabbassi offrono una risonanza profonda e una notevole reattività.',
    restorationsTitle: 'Restauri',
    restorationsIntro: 'Preservare l\'eredità di strumenti pregiati attraverso il restauro esperto.',
    restorationsText: 'Offriamo servizi di restauro professionale per strumenti ad arco antichi e moderni. Dalle riparazioni strutturali al restauro della vernice, ogni strumento riceve la cura e la competenza che merita.',
    galleryTitle: 'Galleria',
    galleryIntro: 'Una selezione di strumenti dal laboratorio.',
    galleryEmpty: 'Nuovi lavori sono in preparazione. Vi preghiamo di tornare presto.',
    contactTitle: 'Contatto',
    contactIntro: 'Interessati a commissionare uno strumento o avete una domanda? Saremo lieti di sentirvi.',
    contactName: 'Nome',
    contactEmail: 'Email',
    contactSubject: 'Oggetto',
    contactMessage: 'Messaggio',
    contactSend: 'Invia Messaggio',
    contactSuccess: 'Il messaggio è stato inviato con successo. Risponderemo il prima possibile.',
    contactError: 'Si è verificato un errore. Riprova.',
    contactRateLimit: 'Troppe richieste. Riprova più tardi.',
    contactNameRequired: 'Inserisci il tuo nome.',
    contactEmailRequired: 'Inserisci il tuo indirizzo email.',
    contactEmailInvalid: 'Inserisci un indirizzo email valido.',
    contactMessageRequired: 'Inserisci il tuo messaggio.',
    contactInquiryType: 'Tipo di richiesta',
    contactCountry: 'Paese (opzionale)',
    contactSending: 'Invio in corso…',
    contactSuccessReference: 'Il tuo codice di riferimento',
    contactSuccessNext: 'Risponderemo entro 48 ore.',
    inquiryTypeGeneral: 'Domanda generale',
    inquiryTypePurchase: 'Acquisto strumento',
    inquiryTypeRestoration: 'Restauro e riparazione',
    inquiryTypeNewBuild: 'Nuova costruzione',
    filterAll: 'Tutti',
    filterViolin: 'Violini',
    filterViola: 'Viole',
    filterCello: 'Violoncelli',
    filterBass: 'Contrabbassi',
    filterAvailable: 'Disponibili',
    instrumentsEmpty: 'Nuovi strumenti in preparazione. Torna presto.',
    inquireNow: 'Richiedi',
    view360: 'Vista a 360°',
    view360Hint: 'Vista interattiva a 360° disponibile',
    specType: 'Tipo',
    specModel: 'Modello',
    specTop: 'Piano armonico',
    specBack: 'Fondo e fasce',
    specVarnish: 'Verniciatura',
    specBodyLength: 'Lunghezza cassa',
    specStringLength: 'Lunghezza corde',
    specYear: 'Anno',
    referenceLabel: 'Rif.',
    priceOnRequest: 'Prezzo su richiesta',
    soldLabel: 'Venduto',
    availableLabel: 'Disponibile',
    backToInstruments: 'Torna agli strumenti',
    inquiryTitle: 'Richiesta per questo strumento',
    inquirySuccess: 'La sua richiesta è stata inviata con successo.',
    inquiryNamePlaceholder: 'Il suo nome',
    inquiryEmailPlaceholder: 'La sua email',
    inquiryMessagePlaceholder: 'Il suo messaggio...',
    inquirySend: 'Invia richiesta',
    inquiryClose: 'Chiudi',
    heroCtaContact: 'Invia una richiesta',
    brandStatementHeadline: 'Costruito per il Suono',
    brandStatementText: 'Ogni strumento inizia con una selezione accurata dei migliori legni tonali — abete e acero, stagionati e risonanti. Da lì, ogni curva, ogni spessore, ogni strato di vernice viene plasmato a mano con un unico scopo: creare uno strumento che canti.',
    brandStatementText2: 'Il lavoro in questo atelier segue le tradizioni dei grandi maestri italiani, affinato da anni di esperienza e dalla ricerca incessante della qualità tonale.',
    futureInstrumentsLabel: 'Strumenti',
    futureInstrumentsDesc: 'Violini, viole, violoncelli e contrabbassi — ciascuno costruito interamente a mano.',
    futureSelectedWorksLabel: 'Opere Selezionate',
    futureSelectedWorksDesc: 'Uno sguardo curato sugli strumenti completati e i loro dettagli.',
    futureAtelierLabel: 'L\'Atelier',
    futureAtelierDesc: 'Strumenti, materiali e il processo dietro ogni creazione.',
    futureAboutLabel: 'Chi Siamo',
    futureAboutDesc: 'Il liutaio, la filosofia e la ricerca del suono eccezionale.',
    featuredSectionTitle: 'Strumenti in Evidenza',
    atelierTeaserTitle: 'L\'Atelier',
    atelierTeaserText: 'Entrate in un laboratorio berlinese dove tradizione e precisione si incontrano. Ogni strumento inizia qui — con legni tonali accuratamente selezionati e le mani di un liutaio dedicato.',
    atelierTeaserCta: 'Scopri l\'Atelier',
    contactTeaserTitle: 'Contattaci',
    contactTeaserText: 'Interessati a commissionare uno strumento, un restauro o semplicemente avete una domanda? Saremo lieti di sentirvi.',
    contactTeaserCta: 'Contatto',
    impressumTitle: 'Note Legali',
    impressumResponsible: 'Responsabile ai sensi del § 5 TMG',
    impressumContact: 'Contatto',
    impressumLiabilityContent: 'Responsabilità per i Contenuti',
    impressumLiabilityContentText: 'In qualità di fornitore di servizi, siamo responsabili dei nostri contenuti su queste pagine in conformità con la legislazione generale.',
    impressumLiabilityLinks: 'Responsabilità per i Link',
    impressumLiabilityLinksText: 'Il nostro sito contiene link a siti web esterni di terze parti sui cui contenuti non abbiamo alcuna influenza.',
    impressumCopyright: 'Diritto d\'Autore',
    impressumCopyrightText: 'I contenuti e le opere create dall\'operatore del sito sono soggetti alla legge sul diritto d\'autore.',
    datenschutzTitle: 'Informativa sulla Privacy',
    datenschutzGeneral: 'Informazioni Generali',
    datenschutzGeneralText: 'La protezione dei vostri dati personali è molto importante per noi.',
    datenschutzCollection: 'Raccolta Dati',
    datenschutzCollectionText: 'Quando visitate il nostro sito, alcuni dati vengono raccolti automaticamente per motivi tecnici.',
    datenschutzForms: 'Moduli di Contatto',
    datenschutzFormsText: 'Quando ci contattate tramite modulo, i dati forniti vengono memorizzati per elaborare la vostra richiesta.',
    datenschutzCookies: 'Cookie',
    datenschutzCookiesText: 'Questo sito utilizza solo cookie tecnicamente necessari.',
    datenschutzRights: 'I Vostri Diritti',
    datenschutzRightsText: 'Avete il diritto di richiedere informazioni sui dati personali memorizzati in qualsiasi momento.',
    footerImprint: 'Note Legali',
    footerPrivacy: 'Privacy',
    errorTitle: 'Qualcosa è andato storto',
    errorText: 'Si è verificato un errore imprevisto. Riprova o torna alla homepage.',
    errorRetry: 'Riprova',
    instrumentInquiryCta: 'Interessato a questo strumento?',
    specsIdentity: 'Identità',
    specsConstruction: 'Costruzione',
    moreInstruments: 'Altri strumenti',
    exploreByType: 'Esplora per tipo',
  },
  fr: {
    brandName: 'Szoradi Instruments',
    tagline: 'Violons, Altos, Violoncelles & Contrebasses d\'Exception — Faits Main à Berlin, Allemagne',
    subline: 'Instruments à cordes artisanaux pour musiciens du monde entier.',
    visitInstagram: 'Visiter Instagram',
    visitFacebook: 'Visiter Facebook',
    followText: 'Suivez Szoradi Instruments sur les réseaux sociaux pour découvrir l\'artisanat, le son et la création.',
    copyright: '© 2025 Szoradi Instruments. Tous droits réservés.',
    imageCaption: 'Violoncelle d\'après Carlo Tononi',
    metaTitle: 'Szoradi Instruments | Violons, Altos, Violoncelles & Contrebasses d\'Exception – Faits Main à Berlin',
    metaDescription: 'Szoradi Instruments fabrique des violons, altos, violoncelles et contrebasses d\'exception faits main à Berlin, Allemagne. Artisanat authentique pour musiciens du monde entier.',
    navHome: 'Accueil',
    navAbout: 'Atelier',
    navAtelier: 'Atelier',
    navInstruments: 'Instruments',
    navRestorations: 'Restaurations',
    navGallery: 'Galerie',
    navContact: 'Contact',
    aboutTitle: 'L\'Atelier',
    aboutIntro: 'Szoradi Instruments est un atelier berlinois dédié à l\'art de la lutherie artisanale.',
    aboutPhilosophy: 'Philosophie',
    aboutPhilosophyText: 'Chaque instrument est une création unique, façonnée par la tradition et affinée par des décennies d\'expérience. Nous croyons qu\'un grand instrument doit chanter de sa propre voix — résonant, expressif et vivant.',
    aboutCraftsmanship: 'Artisanat',
    aboutCraftsmanshipText: 'En utilisant uniquement les meilleurs bois de lutherie et des techniques éprouvées, chaque instrument est méticuleusement fabriqué du début à la fin.',
    atelierTagline: 'L\'Atelier',
    atelierPageTitle: 'Là où le son prend forme',
    atelierPageIntro: 'Un atelier berlinois dédié à l\'art de la lutherie fine — avec précision, patience et une quête intransigeante de l\'excellence sonore.',
    atelierMakerTitle: 'Le Luthier',
    atelierMakerText1: 'Chaque instrument qui quitte cet atelier porte la marque d\'une vision claire: créer des instruments à cordes qui ne sont pas simplement joués, mais profondément ressentis.',
    atelierMakerText2: 'Des années de formation, l\'étude de chefs-d\'œuvre historiques et l\'expérience pratique ont façonné une approche qui allie respect de la tradition et précision moderne.',
    atelierPhilosophyTitle: 'Philosophie',
    atelierPhilosophyText1: 'Un grand instrument doit chanter de sa propre voix — résonant, expressif, vivant.',
    atelierPhilosophyText2: 'Il n\'y a pas de raccourcis. Chaque instrument est construit entièrement à la main, un à la fois.',
    atelierProcessTitle: 'Le Processus',
    atelierProcessText: 'Construire un instrument fin est un voyage qui se mesure en mois, pas en jours.',
    atelierProcessStep1: 'Sélection et préparation',
    atelierProcessStep2: 'Sculpture et graduation',
    atelierProcessStep3: 'Vernissage et mise en voix',
    atelierRestorationTitle: 'Conservation et réparation',
    atelierRestorationText: 'En plus de la construction, l\'atelier offre des services experts de restauration et de réparation pour instruments à cordes fins.',
    instrumentsTitle: 'Instruments',
    instrumentsIntro: 'Chaque instrument est entièrement fait main, suivant les grandes traditions de la lutherie classique.',
    violinTitle: 'Violons',
    violinText: 'Construits sur les modèles des grands maîtres italiens, nos violons offrent un son chaud et projeté avec une jouabilité exceptionnelle.',
    violaTitle: 'Altos',
    violaText: 'Riches, sombres et sonores — nos altos sont conçus pour la profondeur du son et le confort du musicien.',
    celloTitle: 'Violoncelles',
    celloText: 'Puissants et chantants, nos violoncelles sont conçus pour remplir les salles de concert de chaleur et de clarté.',
    bassTitle: 'Contrebasses',
    bassText: 'Du répertoire orchestral au solo, nos contrebasses offrent une résonance profonde et une réactivité remarquable.',
    restorationsTitle: 'Restaurations',
    restorationsIntro: 'Préserver l\'héritage des instruments fins par une restauration experte.',
    restorationsText: 'Nous proposons des services de restauration professionnelle pour les instruments à cordes anciens et modernes.',
    galleryTitle: 'Galerie',
    galleryIntro: 'Une sélection d\'instruments de l\'atelier.',
    galleryEmpty: 'De nouvelles œuvres sont en préparation. Revenez bientôt.',
    contactTitle: 'Contact',
    contactIntro: 'Intéressé par la commande d\'un instrument ou une question ? Nous serions ravis de vous entendre.',
    contactName: 'Nom',
    contactEmail: 'Email',
    contactSubject: 'Sujet',
    contactMessage: 'Message',
    contactSend: 'Envoyer',
    contactSuccess: 'Votre message a été envoyé avec succès. Nous vous répondrons dès que possible.',
    contactError: 'Une erreur s\'est produite. Veuillez réessayer.',
    contactRateLimit: 'Trop de demandes. Veuillez réessayer plus tard.',
    contactNameRequired: 'Veuillez entrer votre nom.',
    contactEmailRequired: 'Veuillez entrer votre adresse email.',
    contactEmailInvalid: 'Veuillez entrer une adresse email valide.',
    contactMessageRequired: 'Veuillez entrer votre message.',
    contactInquiryType: 'Type de demande',
    contactCountry: 'Pays (optionnel)',
    contactSending: 'Envoi en cours…',
    contactSuccessReference: 'Votre code de référence',
    contactSuccessNext: 'Nous vous répondrons sous 48 heures.',
    inquiryTypeGeneral: 'Question générale',
    inquiryTypePurchase: 'Achat d\'instrument',
    inquiryTypeRestoration: 'Restauration et réparation',
    inquiryTypeNewBuild: 'Construction neuve',
    filterAll: 'Tous',
    filterViolin: 'Violons',
    filterViola: 'Altos',
    filterCello: 'Violoncelles',
    filterBass: 'Contrebasses',
    filterAvailable: 'Disponibles',
    instrumentsEmpty: 'De nouveaux instruments sont en préparation. Revenez bientôt.',
    inquireNow: 'Demander',
    view360: 'Vue à 360°',
    view360Hint: 'Vue interactive à 360° disponible',
    specType: 'Type',
    specModel: 'Modèle',
    specTop: 'Table',
    specBack: 'Fond et éclisses',
    specVarnish: 'Vernis',
    specBodyLength: 'Longueur de caisse',
    specStringLength: 'Longueur de corde',
    specYear: 'Année',
    referenceLabel: 'Réf.',
    priceOnRequest: 'Prix sur demande',
    soldLabel: 'Vendu',
    availableLabel: 'Disponible',
    backToInstruments: 'Retour aux instruments',
    inquiryTitle: 'Demande concernant cet instrument',
    inquirySuccess: 'Votre demande a été envoyée avec succès.',
    inquiryNamePlaceholder: 'Votre nom',
    inquiryEmailPlaceholder: 'Votre email',
    inquiryMessagePlaceholder: 'Votre message...',
    inquirySend: 'Envoyer la demande',
    inquiryClose: 'Fermer',
    heroCtaContact: 'Faire une demande',
    brandStatementHeadline: 'Construit pour le Son',
    brandStatementText: 'Chaque instrument commence par une sélection minutieuse des plus beaux bois de lutherie — épicéa et érable, vieillis et résonants. Chaque courbe, chaque épaisseur, chaque couche de vernis est façonnée à la main dans un seul but : créer un instrument qui chante.',
    brandStatementText2: 'Le travail de cet atelier suit les traditions des grands maîtres italiens, affiné par des années d\'expérience et une quête incessante de la qualité tonale.',
    futureInstrumentsLabel: 'Instruments',
    futureInstrumentsDesc: 'Violons, altos, violoncelles et contrebasses — chacun construit entièrement à la main.',
    futureSelectedWorksLabel: 'Œuvres Sélectionnées',
    futureSelectedWorksDesc: 'Un regard curé sur les instruments achevés et leurs détails.',
    futureAtelierLabel: 'L\'Atelier',
    futureAtelierDesc: 'Outils, matériaux et processus derrière chaque instrument.',
    futureAboutLabel: 'À Propos',
    futureAboutDesc: 'Le luthier, la philosophie et la quête d\'un son exceptionnel.',
    featuredSectionTitle: 'Instruments en Vedette',
    atelierTeaserTitle: 'L\'Atelier',
    atelierTeaserText: 'Entrez dans un atelier berlinois où tradition et précision se rencontrent. Chaque instrument commence ici — avec des bois de lutherie soigneusement sélectionnés.',
    atelierTeaserCta: 'Découvrir l\'Atelier',
    contactTeaserTitle: 'Nous Contacter',
    contactTeaserText: 'Intéressé par la commande d\'un instrument, une restauration ou simplement une question ? Nous serons ravis de vous entendre.',
    contactTeaserCta: 'Contact',
    impressumTitle: 'Mentions Légales',
    impressumResponsible: 'Responsable selon § 5 TMG',
    impressumContact: 'Contact',
    impressumLiabilityContent: 'Responsabilité du Contenu',
    impressumLiabilityContentText: 'En tant que prestataire de services, nous sommes responsables de nos propres contenus conformément à la législation générale.',
    impressumLiabilityLinks: 'Responsabilité des Liens',
    impressumLiabilityLinksText: 'Notre site contient des liens vers des sites tiers sur le contenu desquels nous n\'avons aucune influence.',
    impressumCopyright: 'Droit d\'Auteur',
    impressumCopyrightText: 'Les contenus et œuvres créés par l\'exploitant du site sont soumis au droit d\'auteur.',
    datenschutzTitle: 'Politique de Confidentialité',
    datenschutzGeneral: 'Informations Générales',
    datenschutzGeneralText: 'La protection de vos données personnelles est très importante pour nous.',
    datenschutzCollection: 'Collecte de Données',
    datenschutzCollectionText: 'Lorsque vous visitez notre site, certaines données sont automatiquement collectées pour des raisons techniques.',
    datenschutzForms: 'Formulaires de Contact',
    datenschutzFormsText: 'Lorsque vous nous contactez via un formulaire, les données fournies sont stockées pour traiter votre demande.',
    datenschutzCookies: 'Cookies',
    datenschutzCookiesText: 'Ce site n\'utilise que des cookies techniquement nécessaires.',
    datenschutzRights: 'Vos Droits',
    datenschutzRightsText: 'Vous avez le droit de demander des informations sur vos données personnelles à tout moment.',
    footerImprint: 'Mentions Légales',
    footerPrivacy: 'Confidentialité',
    errorTitle: 'Quelque chose s\'est mal passé',
    errorText: 'Une erreur inattendue s\'est produite. Veuillez réessayer ou retourner à la page d\'accueil.',
    errorRetry: 'Réessayer',
    instrumentInquiryCta: 'Intéressé par cet instrument ?',
    specsIdentity: 'Identité',
    specsConstruction: 'Construction',
    moreInstruments: 'Autres instruments',
    exploreByType: 'Explorer par type',
  },
  es: {
    brandName: 'Szoradi Instruments',
    tagline: 'Violines, Violas, Violonchelos & Contrabajos Finos — Hechos a Mano en Berlín, Alemania',
    subline: 'Instrumentos de cuerda artesanales para músicos de todo el mundo.',
    visitInstagram: 'Visitar Instagram',
    visitFacebook: 'Visitar Facebook',
    followText: 'Sigue a Szoradi Instruments en redes sociales para descubrir artesanía, sonido y creación.',
    copyright: '© 2025 Szoradi Instruments. Todos los derechos reservados.',
    imageCaption: 'Violonchelo según Carlo Tononi',
    metaTitle: 'Szoradi Instruments | Violines, Violas, Violonchelos & Contrabajos Finos – Hechos a Mano en Berlín',
    metaDescription: 'Szoradi Instruments construye violines, violas, violonchelos y contrabajos finos hechos a mano en Berlín, Alemania.',
    navHome: 'Inicio',
    navAbout: 'Taller',
    navAtelier: 'Atelier',
    navInstruments: 'Instrumentos',
    navRestorations: 'Restauraciones',
    navGallery: 'Galería',
    navContact: 'Contacto',
    aboutTitle: 'El Taller',
    aboutIntro: 'Szoradi Instruments es un taller berlinés dedicado al arte de la luthería artesanal.',
    aboutPhilosophy: 'Filosofía',
    aboutPhilosophyText: 'Cada instrumento es una creación única, moldeada por la tradición y refinada a través de décadas de experiencia.',
    aboutCraftsmanship: 'Artesanía',
    aboutCraftsmanshipText: 'Utilizando solo las mejores maderas tonales y técnicas consagradas, cada instrumento es meticulosamente fabricado de principio a fin.',
    atelierTagline: 'El Taller',
    atelierPageTitle: 'Donde el sonido toma forma',
    atelierPageIntro: 'Un atelier berlinés dedicado al arte de la luthería fina — con precisión, paciencia y una búsqueda intransigente de la excelencia sonora.',
    atelierMakerTitle: 'El Luthier',
    atelierMakerText1: 'Cada instrumento que sale de este taller lleva la marca de una visión clara: crear instrumentos de cuerda que no solo se toquen, sino que se sientan profundamente.',
    atelierMakerText2: 'Años de formación, estudio de obras maestras históricas y experiencia práctica han moldeado un enfoque que equilibra el respeto por la tradición con la precisión moderna.',
    atelierPhilosophyTitle: 'Filosofía',
    atelierPhilosophyText1: 'Un gran instrumento debe cantar con su propia voz — resonante, expresivo, vivo.',
    atelierPhilosophyText2: 'No hay atajos. Cada instrumento se construye enteramente a mano, uno a la vez.',
    atelierProcessTitle: 'El Proceso',
    atelierProcessText: 'Construir un instrumento fino es un viaje que se mide en meses, no en días.',
    atelierProcessStep1: 'Selección y preparación',
    atelierProcessStep2: 'Tallado y graduación',
    atelierProcessStep3: 'Barnizado y puesta a punto',
    atelierRestorationTitle: 'Conservación y reparación',
    atelierRestorationText: 'Además de la construcción, el taller ofrece servicios expertos de restauración y reparación para instrumentos de cuerda finos.',
    instrumentsTitle: 'Instrumentos',
    instrumentsIntro: 'Cada instrumento está construido enteramente a mano, siguiendo las grandes tradiciones de la luthería clásica.',
    violinTitle: 'Violines',
    violinText: 'Construidos sobre los modelos de los grandes maestros italianos, nuestros violines ofrecen un tono cálido y proyectante.',
    violaTitle: 'Violas',
    violaText: 'Ricas, oscuras y sonoras — nuestras violas están diseñadas para la profundidad del sonido.',
    celloTitle: 'Violonchelos',
    celloText: 'Potentes y cantantes, nuestros violonchelos están diseñados para llenar salas de concierto con calidez y claridad.',
    bassTitle: 'Contrabajos',
    bassText: 'Del repertorio orquestal al solista, nuestros contrabajos ofrecen una resonancia profunda.',
    restorationsTitle: 'Restauraciones',
    restorationsIntro: 'Preservando el legado de instrumentos finos a través de la restauración experta.',
    restorationsText: 'Ofrecemos servicios profesionales de restauración para instrumentos de cuerda antiguos y modernos.',
    galleryTitle: 'Galería',
    galleryIntro: 'Una selección de instrumentos del taller.',
    galleryEmpty: 'Nuevos trabajos están en preparación. Vuelva pronto.',
    contactTitle: 'Contacto',
    contactIntro: '¿Interesado en encargar un instrumento o tiene una pregunta? Nos encantaría saber de usted.',
    contactName: 'Nombre',
    contactEmail: 'Correo electrónico',
    contactSubject: 'Asunto',
    contactMessage: 'Mensaje',
    contactSend: 'Enviar Mensaje',
    contactSuccess: 'Su mensaje ha sido enviado con éxito. Responderemos lo antes posible.',
    contactError: 'Se produjo un error. Inténtelo de nuevo.',
    contactRateLimit: 'Demasiadas solicitudes. Inténtelo más tarde.',
    contactNameRequired: 'Por favor, introduzca su nombre.',
    contactEmailRequired: 'Por favor, introduzca su correo electrónico.',
    contactEmailInvalid: 'Por favor, introduzca un correo electrónico válido.',
    contactMessageRequired: 'Por favor, introduzca su mensaje.',
    contactInquiryType: 'Tipo de consulta',
    contactCountry: 'País (opcional)',
    contactSending: 'Enviando…',
    contactSuccessReference: 'Su código de referencia',
    contactSuccessNext: 'Responderemos en un plazo de 48 horas.',
    inquiryTypeGeneral: 'Pregunta general',
    inquiryTypePurchase: 'Compra de instrumento',
    inquiryTypeRestoration: 'Restauración y reparación',
    inquiryTypeNewBuild: 'Construcción nueva',
    filterAll: 'Todos',
    filterViolin: 'Violines',
    filterViola: 'Violas',
    filterCello: 'Violonchelos',
    filterBass: 'Contrabajos',
    filterAvailable: 'Disponibles',
    instrumentsEmpty: 'Nuevos instrumentos en preparación. Vuelva pronto.',
    inquireNow: 'Consultar',
    view360: 'Vista de 360°',
    view360Hint: 'Vista interactiva de 360° disponible',
    specType: 'Tipo',
    specModel: 'Modelo',
    specTop: 'Tapa',
    specBack: 'Fondo y aros',
    specVarnish: 'Barniz',
    specBodyLength: 'Longitud de caja',
    specStringLength: 'Longitud de cuerda',
    specYear: 'Año',
    referenceLabel: 'Ref.',
    priceOnRequest: 'Precio bajo consulta',
    soldLabel: 'Vendido',
    availableLabel: 'Disponible',
    backToInstruments: 'Volver a instrumentos',
    inquiryTitle: 'Consulta sobre este instrumento',
    inquirySuccess: 'Su consulta ha sido enviada con éxito.',
    inquiryNamePlaceholder: 'Su nombre',
    inquiryEmailPlaceholder: 'Su correo electrónico',
    inquiryMessagePlaceholder: 'Su mensaje...',
    inquirySend: 'Enviar consulta',
    inquiryClose: 'Cerrar',
    heroCtaContact: 'Realizar una consulta',
    brandStatementHeadline: 'Construido para el Sonido',
    brandStatementText: 'Cada instrumento comienza con una cuidadosa selección de las mejores maderas tonales — abeto y arce, envejecidos y resonantes. Cada curva, cada espesor, cada capa de barniz se moldea a mano con un único propósito: crear un instrumento que cante.',
    brandStatementText2: 'El trabajo de este atelier sigue las tradiciones de los grandes maestros italianos, refinado por años de experiencia y la búsqueda inquebrantable de la calidad tonal.',
    futureInstrumentsLabel: 'Instrumentos',
    futureInstrumentsDesc: 'Violines, violas, violonchelos y contrabajos — cada uno construido enteramente a mano.',
    futureSelectedWorksLabel: 'Obras Seleccionadas',
    futureSelectedWorksDesc: 'Una vista curada de los instrumentos completados y sus detalles.',
    futureAtelierLabel: 'El Taller',
    futureAtelierDesc: 'Herramientas, materiales y el proceso detrás de cada instrumento.',
    futureAboutLabel: 'Sobre',
    futureAboutDesc: 'El luthier, la filosofía y la búsqueda del sonido excepcional.',
    featuredSectionTitle: 'Instrumentos Destacados',
    atelierTeaserTitle: 'El Taller',
    atelierTeaserText: 'Adéntrese en un taller berlinés donde la tradición se encuentra con la precisión. Cada instrumento comienza aquí.',
    atelierTeaserCta: 'Descubrir el Taller',
    contactTeaserTitle: 'Contacto',
    contactTeaserText: '¿Interesado en encargar un instrumento, una restauración o simplemente tiene una pregunta? Estaremos encantados de escucharle.',
    contactTeaserCta: 'Contactar',
    impressumTitle: 'Aviso Legal',
    impressumResponsible: 'Responsable según § 5 TMG',
    impressumContact: 'Contacto',
    impressumLiabilityContent: 'Responsabilidad del Contenido',
    impressumLiabilityContentText: 'Como proveedor de servicios, somos responsables de nuestros propios contenidos según la legislación general.',
    impressumLiabilityLinks: 'Responsabilidad de Enlaces',
    impressumLiabilityLinksText: 'Nuestro sitio contiene enlaces a sitios web de terceros sobre cuyo contenido no tenemos influencia.',
    impressumCopyright: 'Derechos de Autor',
    impressumCopyrightText: 'Los contenidos y obras creados por el operador del sitio están sujetos a la ley de derechos de autor.',
    datenschutzTitle: 'Política de Privacidad',
    datenschutzGeneral: 'Información General',
    datenschutzGeneralText: 'La protección de sus datos personales es muy importante para nosotros.',
    datenschutzCollection: 'Recopilación de Datos',
    datenschutzCollectionText: 'Al visitar nuestro sitio web, ciertos datos se recopilan automáticamente por razones técnicas.',
    datenschutzForms: 'Formularios de Contacto',
    datenschutzFormsText: 'Cuando nos contacta a través de un formulario, los datos proporcionados se almacenan para procesar su consulta.',
    datenschutzCookies: 'Cookies',
    datenschutzCookiesText: 'Este sitio web utiliza únicamente cookies técnicamente necesarias.',
    datenschutzRights: 'Sus Derechos',
    datenschutzRightsText: 'Tiene derecho a solicitar información sobre los datos personales almacenados en cualquier momento.',
    footerImprint: 'Aviso Legal',
    footerPrivacy: 'Privacidad',
    errorTitle: 'Algo salió mal',
    errorText: 'Se produjo un error inesperado. Inténtelo de nuevo o vuelva a la página de inicio.',
    errorRetry: 'Reintentar',
    instrumentInquiryCta: '¿Le interesa este instrumento?',
    specsIdentity: 'Identidad',
    specsConstruction: 'Construcción',
    moreInstruments: 'Más instrumentos',
    exploreByType: 'Explorar por tipo',
  },
  ja: {
    brandName: 'Szoradi Instruments',
    tagline: '高級ヴァイオリン、ヴィオラ、チェロ、コントラバス — ドイツ・ベルリンで手作り',
    subline: '世界中の音楽家のための職人による弦楽器。',
    visitInstagram: 'Instagramを見る',
    visitFacebook: 'Facebookを見る',
    followText: 'Szoradi Instrumentsのソーシャルメディアをフォローして、職人技、音、創造をご覧ください。',
    copyright: '© 2025 Szoradi Instruments. 無断転載禁止。',
    imageCaption: 'カルロ・トノーニ様式のチェロ',
    metaTitle: 'Szoradi Instruments | 高級ヴァイオリン、ヴィオラ、チェロ、コントラバス – ドイツ・ベルリンで手作り',
    metaDescription: 'Szoradi Instrumentsは、ドイツ・ベルリンで高級ヴァイオリン、ヴィオラ、チェロ、コントラバスを手作りしています。',
    navHome: 'ホーム',
    navAbout: '工房',
    navAtelier: 'アトリエ',
    navInstruments: '楽器',
    navRestorations: '修復',
    navGallery: 'ギャラリー',
    navContact: 'お問い合わせ',
    aboutTitle: '工房について',
    aboutIntro: 'Szoradi Instrumentsは、手作りの弦楽器製作の芸術に専念するベルリンの工房です。',
    aboutPhilosophy: '哲学',
    aboutPhilosophyText: 'すべての楽器は、伝統によって形作られ、数十年の経験によって洗練されたユニークな作品です。',
    aboutCraftsmanship: '職人技',
    aboutCraftsmanshipText: '最高級のトーンウッドと伝統的な技術のみを使用し、各楽器は最初から最後まで丹念に製作されます。',
    atelierTagline: '工房',
    atelierPageTitle: '音が形になる場所',
    atelierPageIntro: '精密さ、忍耐、そして妥協のない音響的卓越性の追求をもって、手作りの弦楽器製作に捧げるベルリンのアトリエ。',
    atelierMakerTitle: '製作者',
    atelierMakerText1: 'この工房から生まれるすべての楽器には、明確なビジョンの証が刻まれています。単に演奏されるだけでなく、深く感じられる弦楽器を創ること。',
    atelierMakerText2: '長年の研鑽、歴史的名器の研究、そして数百もの名器との実践的な経験が、伝統への敬意と現代の精密さを両立するアプローチを形づくりました。',
    atelierPhilosophyTitle: '哲学',
    atelierPhilosophyText1: '偉大な楽器は自らの声で歌わなければならない — 豊かに響き、表現力に富み、生きている。',
    atelierPhilosophyText2: '近道はありません。各楽器は完全に手作りで、一つずつ製作されます。',
    atelierProcessTitle: 'プロセス',
    atelierProcessText: '高品質な楽器の製作は、日ではなく月で測られる旅です。',
    atelierProcessStep1: '選定と準備',
    atelierProcessStep2: '彫刻と厚み調整',
    atelierProcessStep3: 'ニス塗りと調声',
    atelierRestorationTitle: '保存と修理',
    atelierRestorationText: '新作に加え、弦楽器の専門的な修復・修理サービスも提供しています。',
    instrumentsTitle: '楽器',
    instrumentsIntro: '各楽器は古典的な製作の伝統に従い、完全に手作りで製作されています。',
    violinTitle: 'ヴァイオリン',
    violinText: 'イタリアの偉大な巨匠のモデルに基づいて製作された私たちのヴァイオリンは、温かく響く音色を提供します。',
    violaTitle: 'ヴィオラ',
    violaText: '豊かで深く響く — 私たちのヴィオラは音の深みと演奏者の快適さのために製作されています。',
    celloTitle: 'チェロ',
    celloText: '力強く歌うように、私たちのチェロは温かさと明瞭さでコンサートホールを満たすように設計されています。',
    bassTitle: 'コントラバス',
    bassText: 'オーケストラからソロまで、私たちのコントラバスは深い共鳴と優れた応答性を提供します。',
    restorationsTitle: '修復',
    restorationsIntro: '専門的な修復による名器の遺産の保存。',
    restorationsText: '私たちはアンティークおよび現代の弦楽器のための専門的な修復サービスを提供しています。',
    galleryTitle: 'ギャラリー',
    galleryIntro: '工房からの楽器のセレクション。',
    galleryEmpty: '新しい作品を準備中です。またお越しください。',
    contactTitle: 'お問い合わせ',
    contactIntro: '楽器の注文やご質問がございましたら、お気軽にお問い合わせください。',
    contactName: 'お名前',
    contactEmail: 'メールアドレス',
    contactSubject: '件名',
    contactMessage: 'メッセージ',
    contactSend: '送信',
    contactSuccess: 'メッセージが正常に送信されました。できるだけ早くご返信いたします。',
    contactError: 'エラーが発生しました。もう一度お試しください。',
    contactRateLimit: 'リクエストが多すぎます。後でもう一度お試しください。',
    contactNameRequired: 'お名前を入力してください。',
    contactEmailRequired: 'メールアドレスを入力してください。',
    contactEmailInvalid: '有効なメールアドレスを入力してください。',
    contactMessageRequired: 'メッセージを入力してください。',
    contactInquiryType: 'お問い合わせの種類',
    contactCountry: '国（任意）',
    contactSending: '送信中…',
    contactSuccessReference: 'お問い合わせ番号',
    contactSuccessNext: '48時間以内にご返信いたします。',
    inquiryTypeGeneral: '一般的な質問',
    inquiryTypePurchase: '楽器の購入',
    inquiryTypeRestoration: '修復と修理',
    inquiryTypeNewBuild: '新規製作',
    filterAll: 'すべて',
    filterViolin: 'ヴァイオリン',
    filterViola: 'ヴィオラ',
    filterCello: 'チェロ',
    filterBass: 'コントラバス',
    filterAvailable: '販売中',
    instrumentsEmpty: '新しい楽器を準備中です。またお越しください。',
    inquireNow: 'お問い合わせ',
    view360: '360°ビュー',
    view360Hint: 'インタラクティブな360°ビューが利用可能',
    specType: 'タイプ',
    specModel: 'モデル',
    specTop: '表板',
    specBack: '裏板・横板',
    specVarnish: 'ニス',
    specBodyLength: '胴長',
    specStringLength: '弦長',
    specYear: '製作年',
    referenceLabel: '参照番号',
    priceOnRequest: '価格はお問い合わせください',
    soldLabel: '売約済み',
    availableLabel: '販売中',
    backToInstruments: '楽器一覧に戻る',
    inquiryTitle: 'この楽器についてのお問い合わせ',
    inquirySuccess: 'お問い合わせが送信されました。',
    inquiryNamePlaceholder: 'お名前',
    inquiryEmailPlaceholder: 'メールアドレス',
    inquiryMessagePlaceholder: 'メッセージ...',
    inquirySend: '問い合わせを送信',
    inquiryClose: '閉じる',
    heroCtaContact: 'お問い合わせ',
    brandStatementHeadline: '音のために作る',
    brandStatementText: 'すべての楽器は、最高級のトーンウッド — スプルースとメイプル、熟成され共鳴する木材 — の慎重な選定から始まります。そこから、すべての曲線、すべての厚み、すべてのニスの層が、ひとつの目的のために手で形作られます：歌う楽器を創ること。',
    brandStatementText2: 'このアトリエの仕事は、イタリアの偉大な巨匠たちの伝統に従い、長年の経験と妥協のない音質の追求によって洗練されています。',
    futureInstrumentsLabel: '楽器',
    futureInstrumentsDesc: 'ヴァイオリン、ヴィオラ、チェロ、コントラバス — すべて完全に手作り。',
    futureSelectedWorksLabel: '厳選作品',
    futureSelectedWorksDesc: '完成した楽器とその細部を厳選して紹介。',
    futureAtelierLabel: 'アトリエ',
    futureAtelierDesc: '道具、素材、そしてすべての楽器の背後にあるプロセス。',
    futureAboutLabel: '概要',
    futureAboutDesc: '製作者、哲学、そして卓越した音への追求。',
    featuredSectionTitle: '注目の楽器',
    atelierTeaserTitle: 'アトリエ',
    atelierTeaserText: '伝統と精密さが出会うベルリンの工房へ。すべての楽器はここから始まります。',
    atelierTeaserCta: 'アトリエを見る',
    contactTeaserTitle: 'お問い合わせ',
    contactTeaserText: '楽器の注文、修復、またはご質問がございましたら、お気軽にお問い合わせください。',
    contactTeaserCta: 'お問い合わせ',
    impressumTitle: '特定商取引法に基づく表記',
    impressumResponsible: '運営責任者',
    impressumContact: '連絡先',
    impressumLiabilityContent: 'コンテンツの責任',
    impressumLiabilityContentText: 'サービス提供者として、一般法に従い、これらのページの自社コンテンツに責任を負います。',
    impressumLiabilityLinks: 'リンクの責任',
    impressumLiabilityLinksText: '当サイトには、その内容に影響を与えない外部サードパーティのウェブサイトへのリンクが含まれています。',
    impressumCopyright: '著作権',
    impressumCopyrightText: 'サイト運営者が作成したコンテンツおよび著作物は著作権法の対象となります。',
    datenschutzTitle: 'プライバシーポリシー',
    datenschutzGeneral: '一般情報',
    datenschutzGeneralText: 'お客様の個人データの保護は私たちにとって非常に重要です。',
    datenschutzCollection: 'データ収集',
    datenschutzCollectionText: '当サイトをご訪問の際、技術的な理由から一部のデータが自動的に収集されます。',
    datenschutzForms: 'お問い合わせフォーム',
    datenschutzFormsText: 'フォームからお問い合わせいただいた場合、提供されたデータはお問い合わせの処理のために保存されます。',
    datenschutzCookies: 'Cookie',
    datenschutzCookiesText: '本ウェブサイトは技術的に必要なCookieのみを使用しています。',
    datenschutzRights: 'お客様の権利',
    datenschutzRightsText: 'お客様は、保存されている個人データに関する情報をいつでも要求する権利を有します。',
    footerImprint: '特商法表記',
    footerPrivacy: 'プライバシー',
    errorTitle: 'エラーが発生しました',
    errorText: '予期しないエラーが発生しました。もう一度お試しいただくか、ホームページに戻ってください。',
    errorRetry: '再試行',
    instrumentInquiryCta: 'この楽器にご興味がありますか？',
    specsIdentity: '基本情報',
    specsConstruction: '構造',
    moreInstruments: 'その他の楽器',
    exploreByType: 'タイプ別に探す',
  },
  ko: {
    brandName: 'Szoradi Instruments',
    tagline: '고급 바이올린, 비올라, 첼로 & 더블베이스 — 독일 베를린에서 수제작',
    subline: '전 세계 음악가를 위한 장인의 현악기.',
    visitInstagram: 'Instagram 방문',
    visitFacebook: 'Facebook 방문',
    followText: 'Szoradi Instruments의 소셜 미디어를 팔로우하여 장인정신, 소리, 창작을 발견하세요.',
    copyright: '© 2025 Szoradi Instruments. 모든 권리 보유.',
    imageCaption: '카를로 토노니 양식의 첼로',
    metaTitle: 'Szoradi Instruments | 고급 바이올린, 비올라, 첼로 & 더블베이스 – 독일 베를린에서 수제작',
    metaDescription: 'Szoradi Instruments는 독일 베를린에서 고급 바이올린, 비올라, 첼로, 더블베이스를 수제작합니다.',
    navHome: '홈',
    navAbout: '공방',
    navAtelier: '아틀리에',
    navInstruments: '악기',
    navRestorations: '복원',
    navGallery: '갤러리',
    navContact: '문의',
    aboutTitle: '공방 소개',
    aboutIntro: 'Szoradi Instruments는 수제 현악기 제작의 예술에 전념하는 베를린의 공방입니다.',
    aboutPhilosophy: '철학',
    aboutPhilosophyText: '모든 악기는 전통에 의해 형성되고 수십 년의 경험으로 다듬어진 독창적인 작품입니다.',
    aboutCraftsmanship: '장인정신',
    aboutCraftsmanshipText: '최고급 음향목과 전통 기술만을 사용하여 각 악기를 처음부터 끝까지 꼼꼼하게 제작합니다.',
    atelierTagline: '공방',
    atelierPageTitle: '소리가 형태를 갖추는 곳',
    atelierPageIntro: '정밀함, 인내, 그리고 음향적 탁월함에 대한 타협 없는 추구로 수제 현악기 제작에 헌신하는 베를린 아틀리에.',
    atelierMakerTitle: '제작자',
    atelierMakerText1: '이 공방에서 탄생하는 모든 악기에는 분명한 비전의 흔적이 담겨 있습니다: 단순히 연주되는 것이 아니라 깊이 느껴지는 현악기를 만드는 것.',
    atelierMakerText2: '수년간의 훈련, 역사적 명기의 연구, 수백 대의 명기와의 실무 경험이 전통에 대한 경외와 현대적 정밀함을 균형 있게 결합하는 접근법을 만들었습니다.',
    atelierPhilosophyTitle: '철학',
    atelierPhilosophyText1: '위대한 악기는 자신만의 목소리로 노래해야 합니다 — 울림이 풍부하고, 표현력이 넘치며, 살아있는.',
    atelierPhilosophyText2: '지름길은 없습니다. 각 악기는 완전히 수작업으로, 하나씩 제작됩니다.',
    atelierProcessTitle: '과정',
    atelierProcessText: '훌륭한 악기의 제작은 며칠이 아닌 몇 달로 측정되는 여정입니다.',
    atelierProcessStep1: '선별 및 준비',
    atelierProcessStep2: '조각 및 두께 조정',
    atelierProcessStep3: '니스 및 음색 조정',
    atelierRestorationTitle: '보존 및 수리',
    atelierRestorationText: '신작 외에도 현악기의 전문적인 복원 및 수리 서비스를 제공합니다.',
    instrumentsTitle: '악기',
    instrumentsIntro: '각 악기는 고전적인 악기 제작의 위대한 전통을 따라 완전히 수작업으로 제작됩니다.',
    violinTitle: '바이올린',
    violinText: '이탈리아 거장들의 모델을 기반으로 제작된 우리의 바이올린은 따뜻하고 울림있는 음색을 제공합니다.',
    violaTitle: '비올라',
    violaText: '풍부하고 깊고 울림있는 — 우리의 비올라는 음향의 깊이와 연주자의 편안함을 위해 제작됩니다.',
    celloTitle: '첼로',
    celloText: '강력하고 노래하는 듯한 우리의 첼로는 따뜻함과 명료함으로 콘서트홀을 채우도록 설계되었습니다.',
    bassTitle: '더블베이스',
    bassText: '오케스트라에서 솔로까지, 우리의 더블베이스는 깊은 울림과 뛰어난 반응성을 제공합니다.',
    restorationsTitle: '복원',
    restorationsIntro: '전문적인 복원을 통해 명기의 유산을 보존합니다.',
    restorationsText: '골동품 및 현대 현악기를 위한 전문 복원 서비스를 제공합니다.',
    galleryTitle: '갤러리',
    galleryIntro: '공방의 악기 선택.',
    galleryEmpty: '새로운 작품을 준비 중입니다. 곧 다시 방문해 주세요.',
    contactTitle: '문의',
    contactIntro: '악기 주문이나 질문이 있으시면 연락 주세요.',
    contactName: '이름',
    contactEmail: '이메일',
    contactSubject: '제목',
    contactMessage: '메시지',
    contactSend: '메시지 보내기',
    contactSuccess: '메시지가 성공적으로 전송되었습니다. 가능한 빨리 답변 드리겠습니다.',
    contactError: '오류가 발생했습니다. 다시 시도해 주세요.',
    contactRateLimit: '요청이 너무 많습니다. 나중에 다시 시도해 주세요.',
    contactNameRequired: '이름을 입력해 주세요.',
    contactEmailRequired: '이메일 주소를 입력해 주세요.',
    contactEmailInvalid: '유효한 이메일 주소를 입력해 주세요.',
    contactMessageRequired: '메시지를 입력해 주세요.',
    contactInquiryType: '문의 유형',
    contactCountry: '국가 (선택)',
    contactSending: '전송 중…',
    contactSuccessReference: '참조 코드',
    contactSuccessNext: '48시간 이내에 답변 드리겠습니다.',
    inquiryTypeGeneral: '일반 질문',
    inquiryTypePurchase: '악기 구매',
    inquiryTypeRestoration: '복원 및 수리',
    inquiryTypeNewBuild: '신규 제작',
    filterAll: '전체',
    filterViolin: '바이올린',
    filterViola: '비올라',
    filterCello: '첼로',
    filterBass: '더블베이스',
    filterAvailable: '구매 가능',
    instrumentsEmpty: '새로운 악기를 준비 중입니다. 곧 다시 방문해 주세요.',
    inquireNow: '문의하기',
    view360: '360° 보기',
    view360Hint: '인터랙티브 360° 보기 가능',
    specType: '유형',
    specModel: '모델',
    specTop: '앞판',
    specBack: '뒷판 및 옆판',
    specVarnish: '니스',
    specBodyLength: '몸통 길이',
    specStringLength: '현 길이',
    specYear: '제작 연도',
    referenceLabel: '참조 번호',
    priceOnRequest: '가격 문의',
    soldLabel: '판매 완료',
    availableLabel: '구매 가능',
    backToInstruments: '악기 목록으로',
    inquiryTitle: '이 악기에 대한 문의',
    inquirySuccess: '문의가 성공적으로 전송되었습니다.',
    inquiryNamePlaceholder: '이름',
    inquiryEmailPlaceholder: '이메일',
    inquiryMessagePlaceholder: '메시지...',
    inquirySend: '문의 보내기',
    inquiryClose: '닫기',
    heroCtaContact: '문의하기',
    brandStatementHeadline: '소리를 위해 만들다',
    brandStatementText: '모든 악기는 최고급 음향목 — 가문비나무와 단풍나무, 숙성되고 울림이 있는 목재 — 의 신중한 선택에서 시작됩니다. 거기서부터 모든 곡선, 모든 두께, 모든 니스 층이 하나의 목적을 위해 손으로 만들어집니다: 노래하는 악기를 만드는 것.',
    brandStatementText2: '이 아틀리에의 작업은 이탈리아의 위대한 거장들의 전통을 따르며, 수년간의 경험과 타협 없는 음질 추구로 세련되었습니다.',
    futureInstrumentsLabel: '악기',
    futureInstrumentsDesc: '바이올린, 비올라, 첼로, 더블베이스 — 각각 완전히 수작업으로 제작.',
    futureSelectedWorksLabel: '엄선된 작품',
    futureSelectedWorksDesc: '완성된 악기와 그 세부 사항에 대한 엄선된 시각.',
    futureAtelierLabel: '아틀리에',
    futureAtelierDesc: '도구, 재료, 그리고 모든 악기 뒤에 있는 과정.',
    futureAboutLabel: '소개',
    futureAboutDesc: '제작자, 철학, 그리고 탁월한 소리의 추구.',
    featuredSectionTitle: '추천 악기',
    atelierTeaserTitle: '아틀리에',
    atelierTeaserText: '전통과 정밀함이 만나는 베를린 공방에 오신 것을 환영합니다. 모든 악기는 여기서 시작됩니다.',
    atelierTeaserCta: '아틀리에 보기',
    contactTeaserTitle: '문의하기',
    contactTeaserText: '악기 주문, 복원 또는 질문이 있으시면 편하게 연락해 주세요.',
    contactTeaserCta: '연락하기',
    impressumTitle: '법적 고지',
    impressumResponsible: '책임자',
    impressumContact: '연락처',
    impressumLiabilityContent: '콘텐츠 책임',
    impressumLiabilityContentText: '서비스 제공자로서 일반 법률에 따라 이 페이지의 자체 콘텐츠에 대해 책임을 집니다.',
    impressumLiabilityLinks: '링크 책임',
    impressumLiabilityLinksText: '당사 웹사이트에는 콘텐츠에 영향을 미칠 수 없는 외부 제3자 웹사이트 링크가 포함되어 있습니다.',
    impressumCopyright: '저작권',
    impressumCopyrightText: '사이트 운영자가 만든 콘텐츠 및 저작물은 저작권법의 적용을 받습니다.',
    datenschutzTitle: '개인정보 처리방침',
    datenschutzGeneral: '일반 정보',
    datenschutzGeneralText: '귀하의 개인 데이터 보호는 우리에게 매우 중요합니다.',
    datenschutzCollection: '데이터 수집',
    datenschutzCollectionText: '웹사이트 방문 시 기술적 이유로 일부 데이터가 자동으로 수집됩니다.',
    datenschutzForms: '문의 양식',
    datenschutzFormsText: '양식을 통해 문의하시면 제공된 데이터가 문의 처리를 위해 저장됩니다.',
    datenschutzCookies: '쿠키',
    datenschutzCookiesText: '이 웹사이트는 기술적으로 필요한 쿠키만 사용합니다.',
    datenschutzRights: '귀하의 권리',
    datenschutzRightsText: '귀하는 언제든지 저장된 개인 데이터에 대한 정보를 요청할 권리가 있습니다.',
    footerImprint: '법적 고지',
    footerPrivacy: '개인정보',
    errorTitle: '문제가 발생했습니다',
    errorText: '예기치 않은 오류가 발생했습니다. 다시 시도하거나 홈페이지로 돌아가세요.',
    errorRetry: '다시 시도',
    instrumentInquiryCta: '이 악기에 관심이 있으신가요?',
    specsIdentity: '기본 정보',
    specsConstruction: '구조',
    moreInstruments: '다른 악기',
    exploreByType: '유형별 탐색',
  },
  zh: {
    brandName: 'Szoradi Instruments',
    tagline: '高级小提琴、中提琴、大提琴和低音提琴 — 德国柏林手工制作',
    subline: '为全球音乐家打造的手工弦乐器。',
    visitInstagram: '访问 Instagram',
    visitFacebook: '访问 Facebook',
    followText: '关注 Szoradi Instruments 的社交媒体，探索工艺、声音与创作。',
    copyright: '© 2025 Szoradi Instruments. 保留所有权利。',
    imageCaption: '仿卡罗·托诺尼大提琴',
    metaTitle: 'Szoradi Instruments | 高级小提琴、中提琴、大提琴和低音提琴 – 德国柏林手工制作',
    metaDescription: 'Szoradi Instruments 在德国柏林手工制作高级小提琴、中提琴、大提琴和低音提琴。',
    navHome: '首页',
    navAbout: '工坊',
    navAtelier: '工作室',
    navInstruments: '乐器',
    navRestorations: '修复',
    navGallery: '画廊',
    navContact: '联系',
    aboutTitle: '关于工坊',
    aboutIntro: 'Szoradi Instruments 是一家位于柏林的工坊，致力于手工弦乐器制作的艺术。',
    aboutPhilosophy: '理念',
    aboutPhilosophyText: '每件乐器都是独一无二的作品，由传统塑造，经数十年经验精炼而成。',
    aboutCraftsmanship: '工艺',
    aboutCraftsmanshipText: '仅使用最优质的音木和经过时间考验的技术，每件乐器都经过从头到尾的精心制作。',
    atelierTagline: '工坊',
    atelierPageTitle: '声音成形之处',
    atelierPageIntro: '一间致力于手工弦乐器制作艺术的柏林工作室——以精准、耐心和对卓越音质的不懈追求。',
    atelierMakerTitle: '制琴师',
    atelierMakerText1: '每件离开这间工坊的乐器都承载着一个清晰的愿景：创造不仅仅被演奏，更被深深感受的弦乐器。',
    atelierMakerText2: '多年的训练、对历史名琴的研究以及与数百把名琴的实践经验，塑造了一种兼顾传统敬意与现代精准的制作理念。',
    atelierPhilosophyTitle: '理念',
    atelierPhilosophyText1: '伟大的乐器必须以自己的声音歌唱——共鸣丰富、表现力强、充满生命力。',
    atelierPhilosophyText2: '没有捷径。每件乐器完全手工制作，一次一件。',
    atelierProcessTitle: '制作过程',
    atelierProcessText: '制作一把优质乐器是一段以月计的旅程，而非以天计。',
    atelierProcessStep1: '选材与准备',
    atelierProcessStep2: '雕刻与厚度调整',
    atelierProcessStep3: '上漆与调音',
    atelierRestorationTitle: '保养与修复',
    atelierRestorationText: '除新制外，工坊还提供弦乐器的专业修复与维修服务。',
    instrumentsTitle: '乐器',
    instrumentsIntro: '每件乐器都完全手工制作，遵循古典乐器制作的伟大传统。',
    violinTitle: '小提琴',
    violinText: '以意大利大师的模型为基础，我们的小提琴提供温暖、投射性强的音色。',
    violaTitle: '中提琴',
    violaText: '丰富、深沉、洪亮 — 我们的中提琴为声音深度和演奏者的舒适度而打造。',
    celloTitle: '大提琴',
    celloText: '强劲而歌唱般的，我们的大提琴旨在以温暖和清晰填满音乐厅。',
    bassTitle: '低音提琴',
    bassText: '从管弦乐到独奏，我们的低音提琴提供深沉的共鸣和出色的响应性。',
    restorationsTitle: '修复',
    restorationsIntro: '通过专业修复保存名贵乐器的遗产。',
    restorationsText: '我们为古董和现代弦乐器提供专业修复服务。',
    galleryTitle: '画廊',
    galleryIntro: '工坊乐器精选。',
    galleryEmpty: '新作品正在准备中，请稍后再来。',
    contactTitle: '联系',
    contactIntro: '有兴趣定制乐器或有疑问？我们期待您的来信。',
    contactName: '姓名',
    contactEmail: '电子邮件',
    contactSubject: '主题',
    contactMessage: '留言',
    contactSend: '发送消息',
    contactSuccess: '您的消息已成功发送。我们会尽快回复。',
    contactError: '发生错误，请重试。',
    contactRateLimit: '请求过多，请稍后再试。',
    contactNameRequired: '请输入您的姓名。',
    contactEmailRequired: '请输入您的电子邮件地址。',
    contactEmailInvalid: '请输入有效的电子邮件地址。',
    contactMessageRequired: '请输入您的留言。',
    contactInquiryType: '咨询类型',
    contactCountry: '国家（可选）',
    contactSending: '发送中…',
    contactSuccessReference: '您的参考编号',
    contactSuccessNext: '我们将在48小时内回复。',
    inquiryTypeGeneral: '一般咨询',
    inquiryTypePurchase: '乐器购买',
    inquiryTypeRestoration: '修复与维修',
    inquiryTypeNewBuild: '新建制作',
    filterAll: '全部',
    filterViolin: '小提琴',
    filterViola: '中提琴',
    filterCello: '大提琴',
    filterBass: '低音提琴',
    filterAvailable: '可购买',
    instrumentsEmpty: '新乐器正在准备中，请稍后再来。',
    inquireNow: '咨询',
    view360: '360°视图',
    view360Hint: '可用互动360°视图',
    specType: '类型',
    specModel: '型号',
    specTop: '面板',
    specBack: '背板和侧板',
    specVarnish: '漆面',
    specBodyLength: '琴身长度',
    specStringLength: '弦长',
    specYear: '制作年份',
    referenceLabel: '参考编号',
    priceOnRequest: '价格请咨询',
    soldLabel: '已售出',
    availableLabel: '可购买',
    backToInstruments: '返回乐器列表',
    inquiryTitle: '关于此乐器的咨询',
    inquirySuccess: '您的咨询已成功发送。',
    inquiryNamePlaceholder: '您的姓名',
    inquiryEmailPlaceholder: '您的电子邮件',
    inquiryMessagePlaceholder: '您的留言...',
    inquirySend: '发送咨询',
    inquiryClose: '关闭',
    heroCtaContact: '提交咨询',
    brandStatementHeadline: '为声音而造',
    brandStatementText: '每件乐器都始于对最优质音木的精心挑选——云杉和枫木，经过陈化，充满共鸣。从那里开始，每一条曲线、每一处厚度、每一层漆都由手工塑造，只有一个目的：创造一件能够歌唱的乐器。',
    brandStatementText2: '这个工坊的工作遵循意大利大师们的传统，经过多年的经验和对音质的不懈追求而不断精进。',
    futureInstrumentsLabel: '乐器',
    futureInstrumentsDesc: '小提琴、中提琴、大提琴和低音提琴——每件都完全手工制作。',
    futureSelectedWorksLabel: '精选作品',
    futureSelectedWorksDesc: '对已完成乐器及其细节的精心展示。',
    futureAtelierLabel: '工坊',
    futureAtelierDesc: '工具、材料以及每件乐器背后的过程。',
    futureAboutLabel: '关于',
    futureAboutDesc: '制琴师、理念以及对卓越声音的追求。',
    featuredSectionTitle: '精选乐器',
    atelierTeaserTitle: '工坊',
    atelierTeaserText: '走进柏林工坊，传统与精密在这里相遇。每件乐器都从这里开始。',
    atelierTeaserCta: '探索工坊',
    contactTeaserTitle: '联系我们',
    contactTeaserText: '对乐器定制、修复感兴趣，或有任何问题？我们期待您的来信。',
    contactTeaserCta: '联系',
    impressumTitle: '法律声明',
    impressumResponsible: '根据§5 TMG负责人',
    impressumContact: '联系方式',
    impressumLiabilityContent: '内容责任',
    impressumLiabilityContentText: '作为服务提供商，我们根据一般法律对这些页面上的自有内容负责。',
    impressumLiabilityLinks: '链接责任',
    impressumLiabilityLinksText: '我们的网站包含指向外部第三方网站的链接，我们对其内容无法施加影响。',
    impressumCopyright: '版权',
    impressumCopyrightText: '网站运营者创建的内容和作品受版权法保护。',
    datenschutzTitle: '隐私政策',
    datenschutzGeneral: '一般信息',
    datenschutzGeneralText: '保护您的个人数据对我们非常重要。',
    datenschutzCollection: '数据收集',
    datenschutzCollectionText: '访问我们的网站时，出于技术原因会自动收集某些数据。',
    datenschutzForms: '联系表单',
    datenschutzFormsText: '通过表单联系我们时，您提供的数据将被存储以处理您的请求。',
    datenschutzCookies: 'Cookie',
    datenschutzCookiesText: '本网站仅使用技术上必要的Cookie。',
    datenschutzRights: '您的权利',
    datenschutzRightsText: '您有权随时要求查看存储的个人数据信息。',
    footerImprint: '法律声明',
    footerPrivacy: '隐私政策',
    errorTitle: '出现问题',
    errorText: '发生了意外错误。请重试或返回首页。',
    errorRetry: '重试',
    instrumentInquiryCta: '对这件乐器感兴趣？',
    specsIdentity: '基本信息',
    specsConstruction: '构造',
    moreInstruments: '更多乐器',
    exploreByType: '按类型浏览',
  },
};

export const languageNames: Record<Language, string> = {
  en: 'English',
  de: 'Deutsch',
  it: 'Italiano',
  fr: 'Français',
  es: 'Español',
  ja: '日本語',
  ko: '한국어',
  zh: '中文',
};

export const languageCodes: Record<Language, string> = {
  en: 'en',
  de: 'de',
  it: 'it',
  fr: 'fr',
  es: 'es',
  ja: 'ja',
  ko: 'ko',
  zh: 'zh',
};
